import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, ButtonsContainer } from './CTASection.styles';
import Button from './Button';
import { useGetOaddDealers } from '../../hooks/useGetOaddDealers';
import { OADD_DEALERS_JSON } from '../../constants/environment';
import { getBookTestDriveUrl, getFormsUrl } from '../../utils/ctaUrls';

interface CTASectionProps {
  isSticky?: boolean;
  isMarketUSA: boolean;
  digitalLeadsData: string;
  weblink: string;
  carType?: string;
  kvpsSyncId: string;
  market: string;
  modelYear: number;
  stockNumber: string;
  vehicleId: string;
  audiCode: string;
  isAvailableNow: boolean;
  rapidName: string;
}

// This Component is used only for Tier 1
const CTASection: React.FC<CTASectionProps> = ({
  isSticky,
  isMarketUSA,
  digitalLeadsData,
  weblink,
  carType,
  kvpsSyncId,
  market,
  modelYear,
  stockNumber,
  vehicleId,
  audiCode,
  isAvailableNow,
  rapidName,
}) => {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  // Get Eligible OADD Dealers (dealers that have OADD webistes)
  const { eligibleOaddDealers } = useGetOaddDealers(OADD_DEALERS_JSON, false);
  // Get Book a Test Drive URL
  const bookATestDriveUrl = isMarketUSA
    ? ''
    : getBookTestDriveUrl(
        language,
        market,
        kvpsSyncId,
        modelYear.toString(),
        stockNumber,
        vehicleId,
        rapidName,
      );

  const canadianFormsUrl = isMarketUSA
    ? ''
    : getFormsUrl(
        language,
        market,
        kvpsSyncId,
        stockNumber,
        vehicleId,
        audiCode,
        isAvailableNow,
        rapidName,
      );
  const eligibleDealerIds = useMemo(() => {
    return eligibleOaddDealers.map((item) => item.dealerId);
  }, [eligibleOaddDealers]);

  // @ts-ignore
  const windowVisitor = typeof window !== 'undefined' ? window?.ss?.visitor : '';
  const mcvid = windowVisitor?.getMarketingCloudVisitorID();
  const mcvidParam = mcvid ? `mcvid=${mcvid}` : '';
  const weblinkWithDDC = weblink?.includes('?')
    ? weblink?.concat(`&ddcref=tier1_inventorysearch&${mcvidParam}`)
    : weblink?.concat(`?ddcref=tier1_inventorysearch&${mcvidParam}`);

  const correctDealerListingUrl = useMemo(() => {
    if (eligibleDealerIds.includes(kvpsSyncId)) {
      return eligibleOaddDealers.find((dealer) => dealer.dealerId === kvpsSyncId)?.rootUrl;
    }
    return weblinkWithDDC ?? '';
  }, [eligibleDealerIds, eligibleOaddDealers, kvpsSyncId, weblinkWithDDC]);

  const canadianCtas = useMemo(() => {
    return (
      <ButtonsContainer sticky={isSticky}>
        <Button
          buttonName="CONTACT_DEALER"
          type="primary"
          isMarketUSA={false}
          isSticky={isSticky}
          digitalLeadsData={digitalLeadsData}
          isPreOwned={carType === 'U'}
          contactDealerUrl={canadianFormsUrl}
        />
        <Button
          buttonName="BOOK_TEST_DRIVE"
          type="secondary"
          isMarketUSA={false}
          isSticky={isSticky}
          bookATestDriveUrl={bookATestDriveUrl}
        />
      </ButtonsContainer>
    );
  }, [bookATestDriveUrl, canadianFormsUrl, carType, digitalLeadsData, isSticky]);

  const usCtas = useMemo(() => {
    return (
      <ButtonsContainer sticky={isSticky}>
        <Button
          buttonName="VISIT_DEALER"
          type="primary"
          isMarketUSA
          isSticky={isSticky}
          weblink={weblink}
          correctDealerListingUrl={correctDealerListingUrl}
        />
        <Button
          buttonName="CONTACT_DEALER"
          type="secondary"
          isMarketUSA
          isSticky={isSticky}
          digitalLeadsData={digitalLeadsData}
        />
      </ButtonsContainer>
    );
  }, [correctDealerListingUrl, digitalLeadsData, isSticky, weblink]);

  return (
    <>
      <Container data-testid="cta-container">
        <ButtonsContainer sticky={isSticky}>{isMarketUSA ? usCtas : canadianCtas}</ButtonsContainer>
      </Container>
    </>
  );
};

export default CTASection;
